<template>
  <!-- Enter effects are CSS animations, not <transition>: Vue's JS-driven
       enter left the dialog stuck at opacity 0 (enter-from class never
       removed) when opened from inside another modal's before-close hook. -->
    <div
      v-if="isShow"
      @click="handleClickOverlay"
      class="vc-overlay"
      id="vueConfirm"
    >
        <div ref="vueConfirmDialog" class="vc-container">
          <span class="vc-text-grid">
            <h4 v-if="dialog.title" class="vc-title">{{ dialog.title }}</h4>
            <p v-if="dialog.message" class="vc-text">{{ dialog.message }}</p>
            <span v-if="dialog.auth">
              <input
                v-focus
                v-model="password"
                @keyup.enter="e => handleClickButton(e, true)"
                class="vc-input"
                type="password"
                name="vc-password"
                placeholder="Password"
                autocomplete="off"
              />
            </span>
          </span>
          <div
            class="vc-btn-grid"
            :class="{ isMono: !dialog.button.no || !dialog.button.yes }"
          >
            <button
              v-if="dialog.button.no"
              @click.stop="e => handleClickButton(e, false)"
              class="vc-btn left"
            >
              {{ dialog.button.no }}
            </button>

            <button
              v-if="dialog.button.yes"
              :disabled="dialog.auth ? !password : false"
              @click.stop="e => handleClickButton(e, true)"
              class="vc-btn"
            >
              {{ dialog.button.yes }}
            </button>
          </div>
        </div>
    </div>
</template>

<script>
// Local Vue 3 port of vue-confirm-dialog's component (template/styles
// verbatim; script adapted: local focus directive, emitter bus,
// beforeUnmount, keyup.enter).
import { emitter } from './emitter';

export default {
  name: 'VueConfirmDialog',
  directives: {
    focus: {
      mounted(el) {
        el.focus();
      },
    },
  },
  data() {
    return {
      isShow: false,
      password: null,
      dialog: {
        auth: false,
        title: '',
        message: '',
        button: {},
      },
      params: {},
    };
  },
  methods: {
    resetState() {
      this.password = null;
      this.dialog = {
        auth: false,
        title: '',
        message: '',
        button: {},
        callback: () => {},
      };
    },
    handleClickButton({ target }, confirm) {
      if (target.id == 'vueConfirm') return;
      if (confirm && this.dialog.auth && !this.password) return;
      this.isShow = false;
      if (this.params.callback) {
        this.params.callback(confirm, this.password);
      }
    },
    handleClickOverlay({ target }) {
      if (target.id == 'vueConfirm') {
        this.isShow = false;
        if (this.params.callback) {
          this.params.callback(false, this.password);
        }
      }
    },
    open(params) {
      this.resetState();
      this.params = params;
      this.isShow = true;
      Object.entries(params).forEach((param) => {
        if (typeof param[1] == typeof this.dialog[param[0]]) {
          this.dialog[param[0]] = param[1];
        }
      });
    },
  },
  mounted() {
    emitter.on('open', this.open);
    emitter.on('close', () => {
      this.handleClickOverlay({ target: { id: 'vueConfirm' } });
    });
  },
  beforeUnmount() {
    emitter.off('open', this.open);
  },
};
</script>

<style>
:root {
  --title-color: black;
  --message-color: black;
  --overlay-background-color: #0000004a;
  --container-box-shadow: #0000004a 0px 3px 8px 0px;
  --base-background-color: #ffffff;
  --button-color: #4083ff;
  --button-background-color: #ffffff;
  --button-border-color: #e0e0e0;
  --button-background-color-disabled: #f5f5f5;
  --button-background-color-hover: #f5f5f5;
  --button-box-shadow-active: inset 0 2px 0px 0px #00000014;
  --input-background-color: #ebebeb;
  --input-background-color-hover: #dfdfdf;
  --font-size-m: 16px;
  --font-size-s: 14px;
  --font-weight-black: 900;
  --fw-bold: 700;
  --font-weight-medium: 500;
  --fw-normal: 400;
  --fw-light: 300;
}

.vc-overlay *,
.vc-overlay *:before,
.vc-overlay *:after {
  box-sizing: border-box;
  text-decoration: none;
  -webkit-touch-callout: none;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  padding: 0;
}

.vc-title {
  color: var(--title-color);
  padding: 0 1rem;
  width: 100%;
  font-weight: var(--font-weight-black);
  text-align: center;
  font-size: var(--font-size-m);
  line-height: initial;
  margin-bottom: 5px;
}
.vc-text {
  color: var(--message-color);
  padding: 0 1rem;
  width: 100%;
  font-weight: var(--font-weight-medium);
  text-align: center;
  font-size: var(--font-size-s);
  line-height: initial;
}
.vc-overlay {
  background-color: var(--overlay-background-color);
  width: 100%;
  height: 100%;
  transition: all 0.1s ease-in;
  left: 0;
  top: 0;
  z-index: 999999999999;
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: baseline;
}
.vc-container {
  background-color: var(--base-background-color);
  border-radius: 1rem;
  width: 286px;
  height: auto;
  display: grid;
  grid-template-rows: 1fr max-content;
  box-shadow: var(--container-box-shadow);
}
.vc-text-grid {
  padding: 1rem;
}
.vc-btn-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 0 0 1rem 1rem;
  overflow: hidden;
}
.vc-btn-grid.isMono {
  grid-template-columns: 1fr;
}
.vc-btn {
  border-radius: 0 0 1rem 0;
  color: var(--button-color);
  background-color: var(--button-background-color);
  border: 0;
  font-size: 1rem;
  border-top: 1px solid var(--button-border-color);
  cursor: pointer;
  font-weight: var(--fw-bold);
  outline: none;
  min-height: 50px;
}
.vc-btn:hover {
  background-color: var(--button-background-color-hover);
}
.vc-btn:disabled {
  background-color: var(--button-background-color-disabled);
}
.vc-btn:active {
  box-shadow: var(--button-box-shadow-active);
}
.vc-btn.left {
  border-radius: 0;
  border-right: 1px solid var(--button-border-color);
}
.vc-input[type='password'] {
  width: 100%;
  outline: none;
  border-radius: 8px;
  height: 35px;
  border: 0;
  margin: 5px 0;
  background-color: var(--input-background-color);
  padding: 0 0.5rem;
  font-size: var(--font-size-m);
  transition: 0.21s ease;
}
.vc-input[type='password']:hover,
.vc-input[type='password']:focus {
  background-color: var(--input-background-color-hover);
}

.vc-overlay {
  animation: vc-fade 0.21s ease;
}
.vc-container {
  animation: zoom 0.21s both;
}
@keyframes vc-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes zoom {
  from {
    opacity: 0;
    transform: scale3d(1.1, 1.1, 1.1);
  }
  100% {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}
</style>
